import { useState, useEffect, useCallback, memo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../Logo/LogoSymbol';
import './Header.css';

const serviceLinks = [
  {
    path: '/services/web-development',
    name: 'Next-Gen Development',
    icon: 'fas fa-code'
  },
  {
    path: '/services/mobile',
    name: 'App Innovation Hub',
    icon: 'fas fa-mobile-alt'
  },
  {
    path: '/services/cloud',
    name: 'Cloud Transformation',
    icon: 'fas fa-cloud'
  },
  {
    path: '/services/security',
    name: 'Cyber Shield',
    icon: 'fas fa-shield-alt'
  },
  {
    path: '/services/data',
    name: 'Data Intelligence',
    icon: 'fas fa-database'
  },
  {
    path: '/services/consulting',
    name: 'Digital Acceleration',
    icon: 'fas fa-rocket'
  },
  {
    path: '/services/observability',
    name: 'Observability & Monitoring',
    icon: 'fas fa-chart-line'
  }
];

const productLinks = [
  {
    path: '/products/firmasearch',
    name: 'FirmaSearch',
    icon: 'fas fa-brain',
    external: false
  },
  {
    path: 'https://hiresmart.sradhatech.com',
    name: 'HireSmart',
    icon: 'fas fa-user-tie',
    external: true
  },
  {
    path: '/products/orchix-ai',
    name: 'Orchix Ai',
    icon: 'fas fa-project-diagram',
    external: false
  }
];

const Header = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isPartnershipOpen, setIsPartnershipOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  // Memoize scroll handler
  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  // Handle scroll effect
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);



  // Close menu when clicking outside
  useEffect(() => {
    const closeMenu = (e) => {
      if (isMenuOpen && !e.target.closest('header')) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, [isMenuOpen]);

  const toggleMenu = (e) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleServicesClick = (e) => {
    e.preventDefault();
    navigate('/services');
    setIsMenuOpen(false);
  };

  const handleDropdownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsServicesOpen(!isServicesOpen);
  };

  const handlePartnershipDropdownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPartnershipOpen(!isPartnershipOpen);
  };

  const handleProductsDropdownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsProductsOpen(!isProductsOpen);
  };

  const handleProductsClick = (e) => {
    if (!e.target.closest('.dropdown-arrow-btn')) {
      navigate('/products');
      setIsMenuOpen(false);
      setIsProductsOpen(false);
    }
  };

  const handleWhoWeAreClick = (e) => {
    // Only navigate if not clicking the dropdown arrow
    if (!e.target.closest('.dropdown-arrow-btn')) {
      navigate('/about');
      setIsMenuOpen(false);
      setIsPartnershipOpen(false);
    }
  };

  const handlePartnershipLinkClick = (hash) => {
    setIsMenuOpen(false);
    setIsPartnershipOpen(false);

    // Navigate to about page with hash
    navigate(`/about${hash}`);

    // Small delay to ensure navigation completes before scrolling
    setTimeout(() => {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        const offset = 150; // Account for fixed header + spacing
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Memoize active path check
  const getNavLinkClass = useCallback((path) => {
    const isActive = path === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(path);
    return `nav-link ${isActive ? 'active' : ''}`;
  }, [location.pathname]);

  return (
    <header
      className={`${isScrolled ? 'scrolled' : ''} ${isMenuOpen ? 'menu-open' : ''
        }`}
    >
      <div className="container">
        <div className="header-main">
          <NavLink to="/" className="logo-button" aria-label="Go to home page">
            <Logo />
          </NavLink>
          <button
            type="button"
            className="mobile-menu-btn"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <span className={`hamburger ${isMenuOpen ? 'active' : ''}`}>
              <span className="line" />
              <span className="line" />
              <span className="line" />
            </span>
          </button>
        </div>

        <nav className={isMenuOpen ? 'active' : ''}>
          <ul>
            <li>
              <NavLink
                to="/"
                className={getNavLinkClass('/')}
                onClick={() => setIsMenuOpen(false)}
              >
                <i className="fas fa-home"></i>
                Home
              </NavLink>
            </li>
            <li
              className="services-dropdown"
              onMouseEnter={() => window.innerWidth > 768 && setIsPartnershipOpen(true)}
              onMouseLeave={() => window.innerWidth > 768 && setIsPartnershipOpen(false)}
            >
              <div className="nav-link-wrapper">
                <button
                  type="button"
                  className={`nav-link ${isPartnershipOpen ? 'active' : ''}`}
                  onClick={handleWhoWeAreClick}
                >
                  <i className="fas fa-users"></i>
                  Who We Are
                </button>
                <button
                  type="button"
                  className="dropdown-arrow-btn"
                  onClick={handlePartnershipDropdownClick}
                  aria-label="Toggle who we are menu"
                >
                  <i className={`fas fa-chevron-down dropdown-arrow ${isPartnershipOpen ? 'rotated' : ''}`}></i>
                </button>
              </div>
              <div className={`dropdown-menu ${isPartnershipOpen ? 'show' : ''}`}>
                <button
                  onClick={() => handlePartnershipLinkClick('#almeone-partnership')}
                  className="dropdown-item"
                  type="button"
                >
                  <i className="fas fa-handshake"></i>
                  Our Strategic Alliances
                </button>
              </div>
            </li>
            <li
              className="services-dropdown"
              onMouseEnter={() => window.innerWidth > 768 && setIsServicesOpen(true)}
              onMouseLeave={() => window.innerWidth > 768 && setIsServicesOpen(false)}
            >
              <div className="nav-link-wrapper">
                <button
                  type="button"
                  className={`nav-link ${isServicesOpen ? 'active' : ''}`}
                  onClick={handleServicesClick}
                >
                  <i className="fas fa-cogs"></i>
                  What We Do
                </button>
                <button
                  type="button"
                  className="dropdown-arrow-btn"
                  onClick={handleDropdownClick}
                  aria-label="Toggle services menu"
                >
                  <i className={`fas fa-chevron-down dropdown-arrow ${isServicesOpen ? 'rotated' : ''}`}></i>
                </button>
              </div>
              <div className={`dropdown-menu ${isServicesOpen ? 'show' : ''}`}>
                {serviceLinks.map((service) => (
                  <NavLink
                    key={service.path}
                    to={service.path}
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsServicesOpen(false);
                    }}
                    className="dropdown-item"
                  >
                    <i className={service.icon}></i>
                    {service.name}
                  </NavLink>
                ))}
              </div>
            </li>
            <li
              className="services-dropdown"
              onMouseEnter={() => window.innerWidth > 768 && setIsProductsOpen(true)}
              onMouseLeave={() => window.innerWidth > 768 && setIsProductsOpen(false)}
            >
              <div className="nav-link-wrapper">
                <button
                  type="button"
                  className={`nav-link ${isProductsOpen ? 'active' : ''}`}
                  onClick={handleProductsClick}
                >
                  <i className="fas fa-box-open"></i>
                  Product & Solution
                </button>
                <button
                  type="button"
                  className="dropdown-arrow-btn"
                  onClick={handleProductsDropdownClick}
                  aria-label="Toggle products menu"
                >
                  <i className={`fas fa-chevron-down dropdown-arrow ${isProductsOpen ? 'rotated' : ''}`}></i>
                </button>
              </div>
              <div className={`dropdown-menu ${isProductsOpen ? 'show' : ''}`}>
                {productLinks.map((product) => (
                  product.external ? (
                    <a
                      key={product.path}
                      href={product.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="dropdown-item"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsProductsOpen(false);
                      }}
                    >
                      <i className={product.icon}></i>
                      {product.name}
                    </a>
                  ) : (
                    <NavLink
                      key={product.path}
                      to={product.path}
                      className="dropdown-item"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsProductsOpen(false);
                      }}
                    >
                      <i className={product.icon}></i>
                      {product.name}
                    </NavLink>
                  )
                ))}
              </div>
            </li>
            <li>
              <NavLink
                to="/training"
                className={getNavLinkClass('/training')}
                onClick={() => setIsMenuOpen(false)}
              >
                <i className="fas fa-graduation-cap"></i>
                Learn With Us
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/careers"
                className={getNavLinkClass('/careers')}
                onClick={() => setIsMenuOpen(false)}
              >
                <i className="fas fa-briefcase"></i>
                Join Our Team
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/contact"
                className={getNavLinkClass('/contact')}
                onClick={() => setIsMenuOpen(false)}
              >
                <i className="fas fa-envelope"></i>
                Let&apos;s Connect
              </NavLink>
            </li>
          </ul>
        </nav>

      </div>
    </header>
  );
};

// Memoize the entire component
export default memo(Header);
